import { supabase } from '@/lib/supabase';
import { forgeWorkerUrl, invokeForgeWorker } from '@/lib/forge-worker-client';
import { invokeEdgeFunctionStream } from '@/lib/sse-client';
import type { AnalystMessage } from '@shared/types/company-analyst';
import type { ObjectiveProposal } from '@shared/types/objective-proposals';
import type { WorkspaceObjective, WorkspaceObjectiveInput } from '@shared/types/workspace-objectives';

/** Same staleness rule as the worker: a turn silent for longer is gone. */
const TURN_STALE_MS = 90_000;

export interface GoalsConversation {
  messages: AnalystMessage[];
  proposals: ObjectiveProposal[];
  turnRunning: boolean;
}

const record = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? value as Record<string, unknown> : {};

/** The objectives step's conversation and its pending proposals (managers read them). */
export const loadGoals = async (): Promise<GoalsConversation> => {
  const { data, error } = await supabase.from('qualification_conversations')
    .select('messages, proposals, turn_heartbeat_at').eq('scope', 'goals').maybeSingle();
  if (error) throw new Error('La conversation n’est pas accessible pour le moment.');
  const beat = data?.turn_heartbeat_at ? Date.parse(data.turn_heartbeat_at as string) : null;
  return {
    messages: Array.isArray(data?.messages) ? data!.messages as AnalystMessage[] : [],
    proposals: Array.isArray(data?.proposals) ? data!.proposals as ObjectiveProposal[] : [],
    turnRunning: beat !== null && Date.now() - beat < TURN_STALE_MS,
  };
};

/** One turn (streamed): the analyst proposes or revises the objectives. */
export const runGoalsTurn = async (
  message: string | null, onDelta: (delta: string) => void,
): Promise<{ messages: AnalystMessage[]; proposals: ObjectiveProposal[] }> => {
  if (!forgeWorkerUrl) throw new Error('L’analyste n’est pas configuré sur cet environnement (VITE_FORGE_WORKER_URL).');
  let outcome: { messages: AnalystMessage[]; proposals: ObjectiveProposal[] } | null = null;
  let failure: string | null = null;
  await invokeEdgeFunctionStream({
    url: forgeWorkerUrl,
    body: { action: 'analyst_goals_turn', message },
    onEvent: (name, data) => {
      const payload = record(data);
      if (name === 'delta' && typeof payload.delta === 'string') onDelta(payload.delta);
      else if (name === 'complete') {
        outcome = {
          messages: Array.isArray(payload.messages) ? payload.messages as AnalystMessage[] : [],
          proposals: Array.isArray(payload.proposals) ? payload.proposals as ObjectiveProposal[] : [],
        };
      } else if (name === 'error') failure = typeof payload.message === 'string' ? payload.message : 'La réponse n’a pas abouti.';
    },
  });
  if (failure) throw new Error(failure);
  if (!outcome) throw new Error('La réponse a été coupée. Rechargez pour voir où en est l’analyste.');
  return outcome;
};

export const stopGoalsTurn = async (): Promise<void> => {
  await invokeForgeWorker({ action: 'analyst_goals_stop' });
};

/** Removes resolved proposals from the list; the conversation records them. */
export const resolveGoals = async (
  proposalIds: string[], outcome: 'applied' | 'dismissed', created: Record<string, string> = {},
): Promise<{ messages: AnalystMessage[]; proposals: ObjectiveProposal[] }> => {
  const payload = record(await invokeForgeWorker({ action: 'analyst_goals_resolve', proposalIds, outcome, created }));
  return {
    messages: Array.isArray(payload.messages) ? payload.messages as AnalystMessage[] : [],
    proposals: Array.isArray(payload.proposals) ? payload.proposals as ObjectiveProposal[] : [],
  };
};

/** The objective a proposal writes, with the manager's own rights. */
export const proposalInput = (proposal: ObjectiveProposal, parentObjectiveId: string | null): WorkspaceObjectiveInput => ({
  scope: proposal.ownerUserId ? 'individual' : 'company',
  owner_user_id: proposal.ownerUserId,
  title: proposal.title,
  success_criteria: proposal.measure,
  due_date: proposal.dueDate,
  priority: proposal.priority,
  target: proposal.target,
  baseline: proposal.baseline,
  parent_objective_id: parentObjectiveId,
});

/** The objective a lever's main objective is: active already, or written earlier in the same validation. */
export const parentObjectiveOf = (
  proposal: ObjectiveProposal, objectives: WorkspaceObjective[], created: Readonly<Record<string, string>>,
): string | null => {
  if (!proposal.parentId) return null;
  if (objectives.some((objective) => objective.id === proposal.parentId)) return proposal.parentId;
  const written = created[proposal.parentId];
  if (!written) throw new Error('Validez d’abord l’objectif principal de ce levier.');
  return written;
};

/** Main objectives before their levers, so that a lever finds the objective it produces. */
export const inApplyOrder = (proposals: ObjectiveProposal[]): ObjectiveProposal[] =>
  [...proposals].sort((a, b) => Number(Boolean(a.parentId)) - Number(Boolean(b.parentId)));

/**
 * Applies one proposal through the objectives store (RLS, versions, history) and
 * returns the objective it wrote.
 */
export const applyProposal = async (
  proposal: ObjectiveProposal,
  objectives: WorkspaceObjective[],
  created: Readonly<Record<string, string>>,
  store: {
    save: (input: WorkspaceObjectiveInput, objective?: WorkspaceObjective) => Promise<string>;
    setStatus: (objective: WorkspaceObjective, status: WorkspaceObjective['status']) => Promise<void>;
  },
): Promise<string> => {
  const current = proposal.objectiveId ? objectives.find((objective) => objective.id === proposal.objectiveId) : undefined;
  if (proposal.action !== 'create' && !current) throw new Error('Cet objectif n’existe plus : demandez une nouvelle proposition.');
  if (proposal.action === 'archive') { await store.setStatus(current!, 'archived'); return current!.id; }
  return store.save(proposalInput(proposal, parentObjectiveOf(proposal, objectives, created)), current);
};
